/**
 * The Training page's data menu: one gear button for what staff set up
 * before a run, each entry opening its own dialog.
 *
 *  - Upload data (TRA-1343): publish trajectories as data to train on.
 *  - Held-out tests (TRA-1434): pick the tests the training dataset
 *    validates on.
 *  - Scheduled run (TRA-1475): set when the team's GRPO run starts on
 *    its own.
 *
 * Staff only, like the launch button beside it (TrainingPage).
 */

import { CalendarClock, ListChecks, Settings, Upload } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { HeldOutTestsDialog } from './HeldOutTestsDialog'
import { ScheduledRunDialog } from './ScheduledRunDialog'
import { UploadSetDialog } from './UploadSetDialog'

export function TrainingDataMenu(): React.JSX.Element {
  const [open, setOpen] = useState<'upload' | 'held-out' | 'schedule' | null>(null)
  const close = (): void => {
    setOpen(null)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Training data">
            <Settings className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => { setOpen('upload') }}>
            <Upload className="mr-2 h-4 w-4" />
            Upload data
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => { setOpen('held-out') }}>
            <ListChecks className="mr-2 h-4 w-4" />
            Held-out tests
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => { setOpen('schedule') }}>
            <CalendarClock className="mr-2 h-4 w-4" />
            Scheduled run
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <UploadSetDialog
        open={open === 'upload'}
        onOpenChange={(next) => {
          if (!next) close()
        }}
      />
      {/* Mounted while open: each opening reads the saved tests again. */}
      {open === 'held-out' && <HeldOutTestsDialog onClose={close} />}
      {open === 'schedule' && <ScheduledRunDialog onClose={close} />}
    </>
  )
}
